import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { calculateDistance } from "@/hooks/useLocationSearch";

const PROXIMITY_THRESHOLD_MILES = 0.12; // ~200m
const POLL_INTERVAL_MS = 2 * 60 * 1000; // 2 minutes
const MAX_STAY_MS = 8 * 60 * 60 * 1000; // 8h safety cap
const MAX_GPS_FAILURES = 3;
const STORAGE_KEY = "grounded_active_stay";

interface StayTarget {
  checkInId: string;
  cafeId: string;
  cafeLat: number;
  cafeLng: number;
  checkedInAt: string; // ISO
}

function readTarget(): StayTarget | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as StayTarget) : null;
  } catch {
    return null;
  }
}

function writeTarget(t: StayTarget | null) {
  try {
    if (t) localStorage.setItem(STORAGE_KEY, JSON.stringify(t));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

async function writeCheckout(target: StayTarget, when: Date = new Date()) {
  const checkedInMs = new Date(target.checkedInAt).getTime();
  const minutes = Math.max(
    0,
    Math.round((when.getTime() - checkedInMs) / 60000)
  );
  const { error } = await supabase
    .from("check_ins")
    .update({
      checked_out_at: when.toISOString(),
      stay_duration_minutes: minutes,
    })
    .eq("id", target.checkInId)
    .is("checked_out_at", null);
  return !error;
}

export function StayTrackingProvider() {
  const { user } = useAuth();
  const [target, setTarget] = useState<StayTarget | null>(null);
  const targetRef = useRef<StayTarget | null>(null);
  const intervalRef = useRef<number | null>(null);
  const closingRef = useRef(false);
  const gpsFailuresRef = useRef(0);

  // Keep ref in sync for unload handler
  useEffect(() => {
    targetRef.current = target;
  }, [target]);

  // Load active stay on mount / auth change
  useEffect(() => {
    if (!user) {
      setTarget(null);
      writeTarget(null);
      return;
    }

    let cancelled = false;
    (async () => {
      const sinceIso = new Date(Date.now() - MAX_STAY_MS).toISOString();
      const { data, error } = await supabase
        .from("check_ins")
        .select(
          "id, cafe_id, checked_in_at, checked_out_at, followup_checkin_id, cafes:cafe_id ( latitude, longitude )"
        )
        .eq("user_id", user.id)
        .eq("is_complete", true)
        .is("followup_checkin_id", null)
        .is("checked_out_at", null)
        .gte("checked_in_at", sinceIso)
        .order("checked_in_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (cancelled || error || !data) return;
      const cafe: any = data.cafes;
      if (!cafe?.latitude || !cafe?.longitude) return;

      const next: StayTarget = {
        checkInId: data.id,
        cafeId: data.cafe_id,
        cafeLat: Number(cafe.latitude),
        cafeLng: Number(cafe.longitude),
        checkedInAt: data.checked_in_at,
      };
      writeTarget(next);
      setTarget(next);
    })();

    // Hydrate from storage immediately
    const stored = readTarget();
    if (stored) setTarget(stored);

    return () => {
      cancelled = true;
    };
  }, [user]);

  // Poll GPS / enforce max stay
  useEffect(() => {
    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (!target) return;
    closingRef.current = false;
    gpsFailuresRef.current = 0;

    const closeStay = async (when: Date = new Date()) => {
      if (closingRef.current) return;
      closingRef.current = true;
      const ok = await writeCheckout(target, when);
      if (ok) {
        writeTarget(null);
        setTarget(null);
      } else {
        // allow retry next tick
        closingRef.current = false;
      }
    };

    const tick = async () => {
      if (closingRef.current) return;

      // Safety cap
      const elapsed = Date.now() - new Date(target.checkedInAt).getTime();
      if (elapsed >= MAX_STAY_MS) {
        await closeStay(new Date(new Date(target.checkedInAt).getTime() + MAX_STAY_MS));
        return;
      }

      if (typeof document !== "undefined" && document.hidden) return;
      if (!navigator.geolocation) return;

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          gpsFailuresRef.current = 0;
          const dist = calculateDistance(
            pos.coords.latitude,
            pos.coords.longitude,
            target.cafeLat,
            target.cafeLng
          );
          if (dist >= PROXIMITY_THRESHOLD_MILES) {
            void closeStay();
          }
        },
        () => {
          gpsFailuresRef.current += 1;
          if (gpsFailuresRef.current >= MAX_GPS_FAILURES) {
            void closeStay();
          }
        },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 }
      );
    };

    // First tick after a short delay so we don't immediately close on a stale GPS
    const initial = window.setTimeout(() => void tick(), 30000);
    intervalRef.current = window.setInterval(() => void tick(), POLL_INTERVAL_MS) as unknown as number;

    return () => {
      window.clearTimeout(initial);
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [target]);

  // Best-effort checkout on session end
  useEffect(() => {
    const handler = () => {
      const t = targetRef.current;
      if (!t || closingRef.current) return;
      // Fire-and-forget; not awaited
      void writeCheckout(t);
    };
    window.addEventListener("pagehide", handler);
    window.addEventListener("beforeunload", handler);
    return () => {
      window.removeEventListener("pagehide", handler);
      window.removeEventListener("beforeunload", handler);
    };
  }, []);

  return null;
}
